/**
 * Reads or photographs the pages the model asked for while its turn waits.
 *
 * Each pending view loads in a hidden same-origin frame (the user's own app,
 * logged in as them), which posts back the outline or picture (assistant-view-capture.tsx);
 * this forwards it to the server. A frame that never answers is reported as
 * failed after VIEW_TIMEOUT_MS, so the turn always resumes.
 */
import Box from "@mui/material/Box";
import { useEffect, useRef } from "react";
import {
	type AssistantConversation,
	sendAssistantViewResult,
	type ViewResult,
} from "../../api/assistant/assistant";
import {
	VIEW_MESSAGE_TYPE,
	type ViewCaptureMessage,
	viewEmbedUrl,
} from "./assistant-view-protocol";

/** Covers loading the app in the frame, its data, and the capture itself. */
const VIEW_TIMEOUT_MS = 30_000;
/** The size the page is laid out at, like a laptop screen. */
const FRAME_WIDTH_PX = 1280;
const FRAME_HEIGHT_PX = 900;

const isCaptureMessage = (data: unknown): data is ViewCaptureMessage =>
	typeof data === "object" &&
	data !== null &&
	(data as { type?: unknown }).type === VIEW_MESSAGE_TYPE;

export const AssistantViewRunner = ({
	conversation,
	onUpdate,
	onError,
}: {
	conversation: AssistantConversation;
	onUpdate: (conversation: AssistantConversation) => void;
	onError: (message: string) => void;
}) => {
	const sent = useRef(new Set<string>());
	const pending = conversation.pending_views;

	useEffect(() => {
		if (pending.length === 0) return;
		const waiting = new Set(pending.map((view) => view.tool_use_id));
		const report = (result: ViewResult) => {
			if (
				!waiting.has(result.tool_use_id) ||
				sent.current.has(result.tool_use_id)
			) {
				return;
			}
			sent.current.add(result.tool_use_id);
			sendAssistantViewResult(conversation.id, result)
				.then(onUpdate)
				.catch((err: Error) => onError(err.message));
		};
		const onMessage = (event: MessageEvent) => {
			if (
				event.origin !== window.location.origin ||
				!isCaptureMessage(event.data)
			) {
				return;
			}
			const { token, found, text, png, outline, outline_truncated } = event.data;
			report({
				tool_use_id: token,
				found,
				text,
				png,
				outline,
				outline_truncated,
			});
		};
		window.addEventListener("message", onMessage);
		const timer = window.setTimeout(() => {
			for (const id of waiting) {
				report({
					tool_use_id: id,
					found: false,
					text: `The page did not finish loading within ${VIEW_TIMEOUT_MS / 1000} s`,
					png: "",
					outline: "",
					outline_truncated: false,
				});
			}
		}, VIEW_TIMEOUT_MS);
		return () => {
			window.removeEventListener("message", onMessage);
			window.clearTimeout(timer);
		};
	}, [conversation.id, pending, onUpdate, onError]);

	return (
		<>
			{pending
				.filter((view) => !sent.current.has(view.tool_use_id))
				.map((view) => (
					<Box
						key={view.tool_use_id}
						component="iframe"
						src={viewEmbedUrl(view)}
						title="Page the assistant is looking at"
						aria-hidden
						tabIndex={-1}
						data-component="assistant-view-frame"
						sx={{
							// Laid out at full size so the page renders as on a
							// screen, but invisible and unclickable.
							position: "fixed",
							top: 0,
							left: 0,
							width: FRAME_WIDTH_PX,
							height: FRAME_HEIGHT_PX,
							border: 0,
							opacity: 0,
							pointerEvents: "none",
							zIndex: -1,
						}}
					/>
				))}
		</>
	);
};
